import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CreditCard, Home, KeyRound, LogOut, Settings } from "lucide-react";
import { SidebarNavItem } from "@/components/SidebarNavItem";
import { Button } from "@/components/ui/Button";
import { OgmaLogo } from "@/components/OgmaLogo";
import { useAuthStore } from "@/stores/authStore";
import { cn } from "@/lib/utils";

interface SidebarProps {
  /** Whether the off-canvas sidebar is open on mobile — ignored at the md breakpoint and up. */
  open?: boolean;
}

export function Sidebar({ open = false }: SidebarProps) {
  const { t } = useTranslation();
  const account = useAuthStore((s) => s.account);
  const logout = useAuthStore((s) => s.logout);

  return (
    <aside
      className={cn(
        "fixed top-0 bottom-0 left-0 z-40 flex w-56 shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-transform duration-200 md:static md:top-auto md:bottom-auto md:h-full md:translate-x-0",
        open ? "translate-x-0" : "-translate-x-full",
      )}
    >
      <div className="flex items-center gap-2.5 border-b border-sidebar-border px-3 py-3">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-linear-to-br from-blue-600 to-violet-600">
          <OgmaLogo size={18} className="text-white" />
        </span>
        <span className="text-sm font-semibold text-sidebar-foreground">{t("nav.console")}</span>
      </div>
      <nav className="flex flex-1 flex-col gap-1 px-2 pt-2">
        <SidebarNavItem to="/" icon={Home} label={t("nav.home")} end />
        <SidebarNavItem to="/api-keys" icon={KeyRound} label={t("nav.apiKeys")} />
        <SidebarNavItem to="/subscription" icon={CreditCard} label={t("nav.subscription")} />
      </nav>
      <div className="border-t border-sidebar-border px-2 py-2">
        <div className="truncate px-3 py-1 text-xs text-sidebar-foreground/60">
          {account?.email}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="flex-1 justify-start gap-2.5 text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            render={<Link to="/account" />}
          >
            <Settings className="size-4" />
            {t("nav.myAccount")}
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={logout}
            aria-label={t("nav.logout")}
            title={t("nav.logout")}
            className="text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
          >
            <LogOut />
          </Button>
        </div>
      </div>
    </aside>
  );
}
